// Client for stake-program: PDAs, instruction encoding, account decoding, reward math.
// Wire format is locked by program.test.ts against src/instruction.rs in stake-program.

import { PublicKey, SystemProgram, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { TOKEN_STAKING_PROGRAM_ID } from '../env-variables';

export const PROGRAM_ID: PublicKey | null = TOKEN_STAKING_PROGRAM_ID
  ? new PublicKey(TOKEN_STAKING_PROGRAM_ID)
  : null;

export const POOL_LEN = 285;
export const POSITION_LEN = 168;
export const BPS = 10_000n;
export const YEAR_SECONDS = 31_536_000n;
export const DENOM = BPS * YEAR_SECONDS;

export enum Ix {
  Stake = 2,
  Claim = 3,
  Unstake = 4,
}

export interface Pool {
  mint: PublicKey;
  tokenProgram: PublicKey;
  stakeVault: PublicKey;
  rewardVault: PublicKey;
  authority: PublicKey;
  rateBps: number;
  lockSeconds: number;
  minStake: bigint;
  maxTotalStaked: bigint;
  paused: boolean;
  totalStaked: bigint;
  positionCount: number;
  reservedRewards: bigint;
  totalRewardsPaid: bigint;
  createdAt: bigint;
}

export interface Position {
  pool: PublicKey;
  owner: PublicKey;
  principal: bigint;
  rateBps: number;
  lockSeconds: number;
  stakedAt: bigint;
  unlockAt: bigint;
  lastAccrualAt: bigint;
  accrued: bigint;
  totalClaimed: bigint;
  lockReservation: bigint;
}

function program(): PublicKey {
  if (!PROGRAM_ID) throw new Error('REACT_APP_TOKEN_STAKING_PROGRAM_ID is not set');
  return PROGRAM_ID;
}

export function poolPda(mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from('pool'), mint.toBuffer()], program())[0];
}

export function stakeVaultPda(mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from('vault'), mint.toBuffer()], program())[0];
}

export function rewardVaultPda(mint: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync([Buffer.from('rewards'), mint.toBuffer()], program())[0];
}

export function positionPda(mint: PublicKey, owner: PublicKey): PublicKey {
  return PublicKey.findProgramAddressSync(
    [Buffer.from('position'), mint.toBuffer(), owner.toBuffer()],
    program(),
  )[0];
}

// Byte-level integer codecs: the browser Buffer polyfill lacks the BigInt read/write methods.
function u64(value: bigint): Buffer {
  const b = Buffer.alloc(8);
  for (let i = 0; i < 8; i++) b[i] = Number((value >> BigInt(8 * i)) & 0xffn);
  return b;
}

function readU16(b: Uint8Array, o: number): number {
  return b[o] | (b[o + 1] << 8);
}

function readU32(b: Uint8Array, o: number): number {
  return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16)) + b[o + 3] * 0x1000000;
}

function readU64(b: Uint8Array, o: number): bigint {
  let v = 0n;
  for (let i = 7; i >= 0; i--) v = (v << 8n) | BigInt(b[o + i]);
  return v;
}

function readI64(b: Uint8Array, o: number): bigint {
  const v = readU64(b, o);
  return v >= 1n << 63n ? v - (1n << 64n) : v;
}

const meta = (pubkey: PublicKey, isWritable: boolean, isSigner = false) => ({
  pubkey,
  isSigner,
  isWritable,
});

export function stakeIx(
  owner: PublicKey,
  mint: PublicKey,
  ownerToken: PublicKey,
  tokenProgram: PublicKey,
  amount: bigint,
): TransactionInstruction {
  return new TransactionInstruction({
    programId: program(),
    keys: [
      meta(owner, true, true),
      meta(poolPda(mint), true),
      meta(positionPda(mint, owner), true),
      meta(ownerToken, true),
      meta(stakeVaultPda(mint), true),
      meta(rewardVaultPda(mint), false),
      meta(tokenProgram, false),
      meta(SystemProgram.programId, false),
    ],
    data: Buffer.concat([Buffer.from([Ix.Stake]), u64(amount)]),
  });
}

export function claimIx(
  owner: PublicKey,
  mint: PublicKey,
  ownerToken: PublicKey,
  tokenProgram: PublicKey,
): TransactionInstruction {
  return new TransactionInstruction({
    programId: program(),
    keys: [
      meta(owner, true, true),
      meta(poolPda(mint), true),
      meta(positionPda(mint, owner), true),
      meta(ownerToken, true),
      meta(rewardVaultPda(mint), true),
      meta(tokenProgram, false),
    ],
    data: Buffer.from([Ix.Claim]),
  });
}

export function unstakeIx(
  owner: PublicKey,
  mint: PublicKey,
  ownerToken: PublicKey,
  tokenProgram: PublicKey,
  amount: bigint | null,
): TransactionInstruction {
  const data =
    amount === null
      ? Buffer.from([Ix.Unstake, 0])
      : Buffer.concat([Buffer.from([Ix.Unstake, 1]), u64(amount)]);
  return new TransactionInstruction({
    programId: program(),
    keys: [
      meta(owner, true, true),
      meta(poolPda(mint), true),
      meta(positionPda(mint, owner), true),
      meta(ownerToken, true),
      meta(stakeVaultPda(mint), true),
      meta(rewardVaultPda(mint), true),
      meta(tokenProgram, false),
    ],
    data,
  });
}

class Reader {
  private o = 0;
  constructor(private readonly b: Uint8Array) {}
  u8() { return this.b[this.o++]; }
  bool() { return this.u8() === 1; }
  u16() { const v = readU16(this.b, this.o); this.o += 2; return v; }
  u32() { const v = readU32(this.b, this.o); this.o += 4; return v; }
  u64() { const v = readU64(this.b, this.o); this.o += 8; return v; }
  i64() { const v = readI64(this.b, this.o); this.o += 8; return v; }
  u128() { const lo = this.u64(); const hi = this.u64(); return lo | (hi << 64n); }
  key() { const k = new PublicKey(this.b.subarray(this.o, this.o + 32)); this.o += 32; return k; }
  skip(n: number) { this.o += n; }
}

export function decodePool(data: Uint8Array): Pool {
  if (data.length < POOL_LEN) throw new Error(`pool is ${data.length} bytes`);
  const r = new Reader(data);
  r.skip(2);
  return {
    mint: r.key(),
    tokenProgram: r.key(),
    stakeVault: r.key(),
    rewardVault: r.key(),
    authority: r.key(),
    rateBps: r.u16(),
    lockSeconds: r.u32(),
    minStake: r.u64(),
    maxTotalStaked: r.u64(),
    paused: r.bool(),
    totalStaked: r.u64(),
    positionCount: r.u32(),
    reservedRewards: r.u64(),
    totalRewardsPaid: r.u64(),
    createdAt: r.i64(),
  };
}

export function decodePosition(data: Uint8Array): Position {
  if (data.length < POSITION_LEN) throw new Error(`position is ${data.length} bytes`);
  const r = new Reader(data);
  r.skip(2);
  return {
    pool: r.key(),
    owner: r.key(),
    principal: r.u64(),
    rateBps: r.u16(),
    lockSeconds: r.u32(),
    stakedAt: r.i64(),
    unlockAt: r.i64(),
    lastAccrualAt: r.i64(),
    accrued: r.u128(),
    totalClaimed: r.u64(),
    lockReservation: r.u64(),
  };
}

// SPL layouts: token account amount at 64, mint decimals at 44.
export function tokenAccountAmount(data: Uint8Array): bigint {
  return readU64(data, 64);
}

export function mintDecimals(data: Uint8Array): number {
  return data[44];
}

// Math mirrors stake-program/src/math.rs.

export function accrual(principal: bigint, rateBps: number, elapsedSeconds: bigint): bigint {
  return elapsedSeconds <= 0n ? 0n : principal * BigInt(rateBps) * elapsedSeconds;
}

export function claimableUnits(position: Position, nowSeconds: bigint): bigint {
  const pending = accrual(position.principal, position.rateBps, nowSeconds - position.lastAccrualAt);
  return (position.accrued + pending) / DENOM;
}

export function lockReservation(principal: bigint, rateBps: number, lockSeconds: number): bigint {
  const n = accrual(principal, rateBps, BigInt(lockSeconds));
  return n / DENOM + (n % DENOM === 0n ? 0n : 1n);
}

export function projectedRewards(principal: bigint, rateBps: number, seconds: number): bigint {
  return accrual(principal, rateBps, BigInt(seconds)) / DENOM;
}

// Babel rewrites `**` to Math.pow, which rejects BigInt; build the scale from a string.
function pow10(decimals: number): bigint {
  return BigInt('1' + '0'.repeat(decimals));
}

export function toUnits(tokens: string, decimals: number): bigint {
  const [whole, frac = ''] = tokens.trim().split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  return BigInt(whole || '0') * pow10(decimals) + BigInt(padded || '0');
}

export function formatUnits(units: bigint, decimals: number, maxFraction = decimals): string {
  const scale = pow10(decimals);
  const whole = units / scale;
  const frac = (units % scale)
    .toString()
    .padStart(decimals, '0')
    .slice(0, maxFraction)
    .replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole.toString();
}

export function formatBps(rateBps: number): string {
  return `${(rateBps / 100).toFixed(2)}%`;
}
